import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import zxcvbn from "zxcvbn";
import Loader from "../components/Loader";
import {
  useLazyValidateResetPasswordTokenQuery,
  useResetPasswordMutation,
} from "../slices/usersApiSlice";

const ResetPasswordScreen = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [score, setScore] = useState(0);
  const [tokenStatus, setTokenStatus] = useState("checking");
  const [tokenMessage, setTokenMessage] = useState("Checking reset link...");
  const [validateToken, { isLoading: isCheckingToken }] = useLazyValidateResetPasswordTokenQuery();
  const [resetPassword, { isLoading }] = useResetPasswordMutation();

  useEffect(() => {
    const run = async () => {
      if (!token) {
        setTokenStatus("invalid");
        setTokenMessage("Reset token is missing.");
        return;
      }
      try {
        await validateToken(token).unwrap();
        setTokenStatus("valid");
        setTokenMessage("");
      } catch (err) {
        setTokenStatus("invalid");
        setTokenMessage(err?.data?.message || "Reset link is invalid.");
      }
    };
    run();
  }, [token, validateToken]);

  const submitHandler = async (e) => {
    e.preventDefault();
    if (!token || tokenStatus !== "valid") {
      toast.error("Reset token is missing");
      return;
    }
    if (!password || password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (zxcvbn(password).score < 3) {
      toast.error("Please choose a stronger password");
      return;
    }

    try {
      const res = await resetPassword({ token, password }).unwrap();
      toast.success(res?.message || "Password reset successful");
      navigate("/login");
    } catch (err) {
      toast.error(err?.data?.message || err?.error || "Password reset failed");
    }
  };

  return (
    <section className="mx-auto w-full max-w-md">
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm md:p-8">
        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">
          Account
        </p>
        <h1 className="mb-3 text-3xl font-semibold text-slate-900">Reset Password</h1>

        {isCheckingToken || tokenStatus === "checking" ? <Loader /> : null}

        {tokenStatus === "invalid" ? (
          <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {tokenMessage}
          </div>
        ) : null}

        {tokenStatus === "valid" ? (
          <form onSubmit={submitHandler} className="space-y-4">
            <div>
              <label htmlFor="newPassword" className="mb-1.5 block text-sm font-medium text-slate-700">
                New Password
              </label>
              <input
                id="newPassword"
                type="password"
                value={password}
                onChange={(e) => {
                  const val = e.target.value;
                  setPassword(val);
                  setScore(zxcvbn(val).score);
                }}
                placeholder="Enter new password"
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-sky-300 focus:bg-white focus:ring-2 focus:ring-sky-100"
              />
              <p className={`mt-1 text-xs font-medium ${score < 3 ? "text-rose-600" : "text-emerald-600"}`}>
                {score < 3 ? "Weak password" : "Strong password"}
              </p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="mb-1.5 block text-sm font-medium text-slate-700">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-sky-300 focus:bg-white focus:ring-2 focus:ring-sky-100"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex items-center justify-center rounded-xl bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              Reset password
            </button>
            {isLoading ? <Loader /> : null}
          </form>
        ) : null}

        <p className="mt-5 text-sm text-slate-600">
          Back to{" "}
          <Link to="/login" className="font-semibold text-sky-700 no-underline hover:text-sky-800">
            Sign In
          </Link>
        </p>
      </div>
    </section>
  );
};

export default ResetPasswordScreen;
